import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './ImageLightbox.css';

/*
 * App-wide image lightbox. Contender thumbnails on matchups, brackets,
 * etc. render small + cover-cropped, so an uploaded image (a stat card,
 * a screenshot) is unreadable at thumbnail size. Tapping one opens this
 * full-screen overlay showing the whole image (object-fit: contain).
 *
 * A single provider at the app root renders one overlay; any descendant
 * calls useImageLightbox().openImage(src, alt) — or just uses
 * <ZoomableImage> — without prop-drilling. Closes on backdrop tap, the
 * × button, or Esc.
 */

const ImageLightboxContext = createContext({ openImage: () => {} });

export const useImageLightbox = () => useContext(ImageLightboxContext);

export function ImageLightboxProvider({ children }) {
  const [image, setImage] = useState(null); // { src, alt } | null

  const openImage = useCallback((src, alt = '') => {
    if (!src) return;
    setImage({ src, alt });
  }, []);
  const close = useCallback(() => setImage(null), []);

  // Esc to close + lock background scroll while the overlay is up.
  useEffect(() => {
    if (!image) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [image, close]);

  return (
    <ImageLightboxContext.Provider value={{ openImage }}>
      {children}
      {image &&
        createPortal(
          <div
            className="img-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Enlarged image"
            onClick={close}
          >
            <button
              type="button"
              className="img-lightbox__close"
              aria-label="Close"
              onClick={close}
            >
              ×
            </button>
            {/* stopPropagation so a tap ON the image doesn't close it —
                only the backdrop does. */}
            <img
              className="img-lightbox__img"
              src={image.src}
              alt={image.alt || ''}
              onClick={(e) => e.stopPropagation()}
            />
          </div>,
          document.body,
        )}
    </ImageLightboxContext.Provider>
  );
}

/*
 * Drop-in replacement for a thumbnail <img> that opens the lightbox on
 * click. Crucially stops propagation so tapping the image inside a
 * clickable card (vote / select / navigate) enlarges rather than firing
 * the card's action.
 */
export function ZoomableImage({ src, alt = '', className = '', imgProps = {} }) {
  const { openImage } = useImageLightbox();
  if (!src) return null;
  const open = (e) => {
    e.stopPropagation();
    e.preventDefault();
    openImage(src, alt);
  };
  return (
    <span
      className="zoomable-image"
      role="button"
      tabIndex={0}
      aria-label={alt ? `Enlarge image of ${alt}` : 'Enlarge image'}
      title="Tap to enlarge"
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') open(e);
      }}
    >
      <img src={src} alt={alt} className={className} decoding="async" loading="lazy" {...imgProps} />
      <span className="zoomable-image__hint" aria-hidden="true">⤢</span>
    </span>
  );
}
